import { AxisCode } from '@atlas/types'
import { Field, SectionCard } from '@atlas/ui-components'
import { Button, Combobox } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { BrandPage } from '@/domain/explore.types'
import { useUpdateBrand } from '@/hooks/useCatalogWrites'
import { useValuesByAxisCode } from '@/hooks/useClassification'
import type { BrandMetaLink } from './brand-meta'
import { linksOf, taglineOf } from './brand-meta'
import { MarketNames } from './MarketNames'
import { MetaJsonEditor } from './MetaJsonEditor'
import { TaxonomyMetaEditor } from './TaxonomyMetaEditor'

/**
 * Editing the brand ROW: the fields ATLAS owns about the brand itself, as opposed to the
 * articles, assets and rules the other tabs read.
 *
 * `code` is deliberately absent. It is the code of the brand's axis value too, so changing
 * it would rename a taxonomy value every classification already points at — a merge, not
 * an edit, and the merge control on the Meta tab is where that decision belongs.
 */

/**
 * A link row while it is being edited. Carries a `rowId` the stored shape has no need for:
 * the label is editable, so keying the rendered rows on it would remount the field the
 * user is typing in, and keying on the array index breaks as soon as a row is removed.
 */
type LinkRow = BrandMetaLink & { rowId: string }

/** The editable shape, flattened out of `brand` + `brand.meta` and back again on save. */
interface BrandForm {
	name: string
	description: string
	businessUnit: string
	tagline: string
	links: LinkRow[]
}

let nextRowId = 0
const withRowId = (link: BrandMetaLink): LinkRow => ({ ...link, rowId: `link-${nextRowId++}` })

const formOf = (page: BrandPage): BrandForm => ({
	name: page.brand.name,
	description: page.brand.description ?? '',
	businessUnit: page.brand.businessUnit ?? '',
	tagline: taglineOf(page.brand.meta)?.text ?? '',
	links: linksOf(page.brand.meta).map(withRowId),
})

/**
 * The `meta` to send: the existing object with only the keys this form owns replaced.
 *
 * Spread from the stored `meta`, never rebuilt — a brand carries fields no form renders
 * (`caution`, `pillar`, the taxonomy load's own bookkeeping), and rebuilding would delete
 * every one of them on the first save.
 */
function metaOf(stored: Record<string, unknown>, form: BrandForm): Record<string, unknown> {
	const next = { ...stored }
	if (form.tagline.trim()) next.tagline = { text: form.tagline.trim() }
	else delete next.tagline
	// `rowId` is an editing artefact and must not reach the stored shape.
	const links = form.links.map(({ rowId: _rowId, ...link }) => link)
	if (links.length > 0) next.links = links
	else delete next.links
	return next
}

function LinkRows({ links, onChange }: { links: LinkRow[]; onChange: (next: LinkRow[]) => void }) {
	const patch = (rowId: string, part: Partial<BrandMetaLink>) =>
		onChange(links.map((link) => (link.rowId === rowId ? { ...link, ...part } : link)))

	return (
		<div className="space-y-2">
			{links.map((link) => (
				<div key={link.rowId} className="flex flex-wrap items-end gap-2">
					<Field
						label="Label"
						value={link.label}
						onChange={(e) => patch(link.rowId, { label: e.target.value })}
						className="min-w-40 flex-1"
					/>
					<Field
						label="URL"
						value={link.url ?? ''}
						placeholder="Leave empty if not known"
						onChange={(e) => patch(link.rowId, { url: e.target.value.trim() || null })}
						className="min-w-60 flex-[2]"
					/>
					<Button
						type="button"
						variant="secondary"
						className="rounded-ds-full"
						onPress={() => onChange(links.filter((l) => l.rowId !== link.rowId))}
					>
						Remove
					</Button>
				</div>
			))}
			<Button
				type="button"
				variant="secondary"
				className="rounded-ds-full"
				onPress={() => onChange([...links, withRowId({ label: '', url: null })])}
			>
				Add link
			</Button>
		</div>
	)
}

export function BrandEditTab({ page, market }: { page: BrandPage; market: string | null }) {
	const update = useUpdateBrand(page.brand.brandId, market)
	// The units are rows on the `business_unit` axis, not a list this file owns — adding one
	// is an INSERT an administrator makes, and hardcoding them here would make it a release.
	const { data: units = [] } = useValuesByAxisCode(AxisCode.BUSINESS_UNIT)
	const [form, setForm] = useState<BrandForm>(() => formOf(page))
	const set = (part: Partial<BrandForm>) => setForm((current) => ({ ...current, ...part }))

	const save = () => {
		// Empty means "not recorded", which the column stores as NULL — an empty string would
		// render as a present-but-blank value everywhere the page reads it.
		update.mutate({
			name: form.name.trim(),
			description: form.description.trim() || null,
			businessUnit: form.businessUnit.trim() || null,
			// Links with no label are dropped, matching what `linksOf` will read back.
			meta: metaOf(page.brand.meta, { ...form, links: form.links.filter((l) => l.label.trim()) }),
		})
	}

	return (
		<div className="space-y-4">
			<SectionCard title="Brand" description="What ATLAS records about the brand itself.">
				<div className="grid gap-3 sm:grid-cols-2">
					<Field label="Name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
					<div>
						<span className="mb-1 block text-ds-text-muted text-xs">Business unit</span>
						<Combobox
							aria-label="Business unit"
							value={form.businessUnit || null}
							onChange={(businessUnit) => set({ businessUnit: businessUnit ?? '' })}
							// A brand without one is a real state, so the empty choice is named
							// rather than left as a blank the reader has to interpret.
							placeholder="Not recorded"
							options={units.map((u) => ({ value: u.code, label: u.label }))}
						/>
					</div>
				</div>
				<Field
					label="Tagline"
					value={form.tagline}
					description="The brand's leitmotiv, as it appears in its own materials."
					onChange={(e) => set({ tagline: e.target.value })}
					className="mt-3"
				/>
				<Field
					label="Description"
					value={form.description}
					onChange={(e) => set({ description: e.target.value })}
					className="mt-3"
				/>
			</SectionCard>

			<SectionCard title="Collections" description="Where this brand's assets live outside ATLAS.">
				<LinkRows links={form.links} onChange={(links) => set({ links })} />
			</SectionCard>

			<MarketNames
				valueId={page.brand.valueId}
				subject="brand"
				defaultName={page.brand.name}
				mappings={page.namesByMarket}
			/>

			{/* Two objects: the catalog row's meta and the axis value's. The Meta tab reads
			    both, so both are editable here. */}
			<MetaJsonEditor
				meta={page.brand.meta}
				subject="brand"
				title="Raw meta — catalog row"
				description="What the brand row itself stores. Written by this page and by the seeders."
				saving={update.isPending}
				onSave={(meta) => update.mutate({ meta })}
			/>

			<TaxonomyMetaEditor valueId={page.brand.valueId} meta={page.brandValueMeta} subject="brand" />

			<div className="flex justify-end gap-2">
				<Button
					type="button"
					variant="secondary"
					className="rounded-ds-full"
					isDisabled={update.isPending}
					onPress={() => setForm(formOf(page))}
				>
					Reset
				</Button>
				<Button
					type="button"
					className="rounded-ds-full px-5"
					isDisabled={update.isPending || !form.name.trim()}
					onPress={save}
				>
					{update.isPending ? 'Saving…' : 'Save'}
				</Button>
			</div>
		</div>
	)
}
